import React from 'react';
import { OmnibarEntry } from '../omnibar-interfaces';
import { ResourceOperations, ResourceTypes } from '../../../authentication/resources';
import { bookInterviewV2Path, citizenAreaPathName, dfdgHistoryViewerPath, negativeEventCreatePath } from '../../../areas/citizen/authority/citizen-authority-area-pathnames';
import { OmnibarChangeJobcenter } from '../controls/omnibar-change-jobcenter';
import { OPTIC_URL_PARAM } from '../../../areas/citizen/authority/authority-optic/authority-optic-context';
import { catalogCorrectionsToggle } from '../utils/catalog-corrections';

const functions: OmnibarEntry[] = [
  {
    id: '8d57d273-7f8c-4a44-961b-ac9cf92e4169',
    label: 'Hop til...',
    context: 'citizen',
    group: 'Borgerområde',
    dynamicLinksAction: (contextId: string) => ({
      resourceType: 'Case',
      resourceId: contextId,
    }),
  },
  {
    id: 'ca111549-f8f2-4b71-80d7-c6d876640192',
    label: 'A-kasse',
    context: 'citizen',
    group: 'Borgerområde',
    url: '/deeplink/citizen/:contextId/unemploymentbenefits',
    resourceActions: [{ resourceOperation: ResourceOperations.Read, resourceType: ResourceTypes.Citizen }],
    employmentOpticOnly: true,
    hideForAnonymousCitizen: true,
  },
  {
    id: '6f887d9b-3080-4469-9cb3-9f7c2a265d00',
    label: 'Asyl & Integration',
    context: 'citizen',
    group: 'Borgerområde',
    url: '/deeplink/citizen/:contextId/integration-details',
    resourceActions: [{ resourceOperation: ResourceOperations.Read, resourceType: ResourceTypes.Citizen }],
    employmentOpticOnly: true,
    hideForAnonymousCitizen: true,
  },
  {
    id: 'ca111374-f8f2-4b71-80d7-c6d876640192',
    label: 'Borgers CV',
    context: 'citizen',
    group: 'Borgerområde',
    url: '/deeplink/citizen/:contextId/cvjobsearch/cvviewer',
    resourceActions: [{ resourceOperation: ResourceOperations.Read, resourceType: ResourceTypes.Citizen }],
    employmentOpticOnly: true,
    hideForAnonymousCitizen: true,
  },
  {
    id: 'e387348d-3513-4417-99eb-697680b8b721',
    label: 'Borgere',
    context: 'none',
    group: 'Borgerområde',
    url: '/deeplink/citizens',
    resourceActions: [{ resourceOperation: ResourceOperations.Read, resourceType: ResourceTypes.Citizen }],
  },
  {
    id: 'eea29d20-bfb8-4f5e-9eb3-2cb102a3bb6e',
    label: 'Borgeroverblik',
    context: 'citizen',
    group: 'Borgerområde',
    url: '/deeplink/citizen/:contextId/details',
    resourceActions: [{ resourceOperation: ResourceOperations.Read, resourceType: ResourceTypes.Citizen }],
  },
  {
    id: '34118845-91fb-40e3-8674-42c51484ef2c',
    label: 'Dan brev',
    keywords: ['skrivelse', 'digital post'],
    context: 'citizen',
    group: 'Borgerhandling',
    callbackFn: (contextId: string, _crmUrl, _tenantName, _spuid, opticId?: string) => {
      const baseUrl = `/deeplink/embedded/citizen/${contextId}/communication/guide-letter`;
      window.open(opticId ? `${baseUrl}?${OPTIC_URL_PARAM}=${opticId}` : baseUrl);
    },
    resourceActions: [{ resourceOperation: ResourceOperations.Write, resourceType: ResourceTypes.CitizenLetter }],
  },
  {
    id: '34234545-91fb-40e3-8674-42c51484ef2c',
    label: 'Dan blanket',
    keywords: ['skema', 'formular'],
    context: 'citizen',
    group: 'Borgerhandling',
    callbackFn: (contextId: string) => {
      window.open(`/deeplink/embedded/citizen/${contextId}/communication/guide-form`);
    },
    resourceActions: [{ resourceOperation: ResourceOperations.Write, resourceType: ResourceTypes.CitizenLetter }],
  },
  {
    id: '0ce2e9ef-fb2d-453e-b51b-a44b340c546f',
    label: 'Dan dokument',
    context: 'citizen',
    group: 'Borgerhandling',
    callbackFn: (contextId: string, _crmUrl, _tenantName, _spuid, opticId?: string) => {
      const baseUrl = `/deeplink/citizen/${contextId}/journal/document/create`;
      window.open(opticId ? `${baseUrl}?${OPTIC_URL_PARAM}=${opticId}` : baseUrl);
    },
    resourceActions: [{ resourceOperation: ResourceOperations.Write, resourceType: ResourceTypes.Citizen }],
  },
  {
    id: '0ce2e9ef-fb2d-453e-b51b-a44b340c1234',
    label: 'Redigér plan',
    keywords: ['handleplan', 'min plan'],
    context: 'citizen',
    group: 'Borgerhandling',
    callbackFn: (contextId: string) => {
      window.open(`/deeplink/citizen/${contextId}/plan/edit`);
    },
    resourceActions: [{ resourceOperation: ResourceOperations.Write, resourceType: ResourceTypes.Plan }],
    employmentOpticOnly: true,
  },
  {
    id: '4c20be02-a758-483e-916d-6c94d914b9c6',
    label: 'Historik',
    context: 'citizen',
    group: 'Borgerområde',
    url: '/deeplink/citizen/:contextId/history',
    resourceActions: [{ resourceOperation: ResourceOperations.Read, resourceType: ResourceTypes.Citizen }],
  },
  {
    id: '5cb9cefc-441a-4218-9449-e74edcb9bbb8',
    label: 'CV & Jobsøgning',
    context: 'citizen',
    group: 'Borgerområde',
    url: '/deeplink/citizen/:contextId/cvjobsearch',
    resourceActions: [{ resourceOperation: ResourceOperations.Read, resourceType: ResourceTypes.Citizen }],
    employmentOpticOnly: true,
    hideForAnonymousCitizen: true,
  },
  {
    id: '5cb9cefc-441a-4218-9109-e74e22b9bbb8',
    label: 'Opret Besked til A-kasse',
    context: 'citizen',
    group: 'Borgerområde',
    url: '/deeplink/citizen/:contextId/citizenmessage',
    tenantAddonEnabled: ['CreateUnemploymentFundMessages'],
    resourceActions: [{ resourceOperation: ResourceOperations.Read, resourceType: ResourceTypes.Citizen }],
    employmentOpticOnly: true,
    hideForAnonymousCitizen: true,
  },
  {
    id: '1247277f-39ff-445b-8758-80070c95eada',
    label: 'Journal & dokumenter',
    keywords: ['notater', 'breve'],
    context: 'citizen',
    group: 'Borgerområde',
    url: '/deeplink/citizen/:contextId/journal',
    resourceActions: [{ resourceOperation: ResourceOperations.Read, resourceType: ResourceTypes.Citizen }],
  },
  {
    id: '8df305a1-86a2-4e98-8567-f7536ea8aafc',
    label: 'Opgaver',
    context: 'none',
    group: 'Borgerområde',
    url: '/deeplink/citizens/tasks',
    resourceActions: [{ resourceOperation: ResourceOperations.Read, resourceType: ResourceTypes.CitizenTask }],
  },
  {
    id: '3f129728-AC3Q-4d65-94b9-c5a7c03fb022',
    label: 'Samtaleforberedelse: Jobparate',
    context: 'citizen',
    group: 'Borgerhandling',
    callbackFn: (contextId: string) => {
      window.open(`/deeplink/citizen/${contextId}/interview-preparation-job-ready`);
    },
    resourceActions: [{ resourceOperation: ResourceOperations.Read, resourceType: ResourceTypes.Interview }],
    tenantAddonEnabled: ['samtaleforberedelse'],
    isPossiblyRestricted: true,
    employmentOpticOnly: true,
    hideForAnonymousCitizen: true,
  },
  {
    id: '3f129728-AC31-4d65-94b9-c5a7c03fb022',
    label: 'Samtaleforberedelse: Sygemeldte',
    context: 'citizen',
    group: 'Borgerhandling',
    callbackFn: (contextId: string) => {
      window.open(`/deeplink/citizen/${contextId}/interview-preparation-sickness`);
    },
    resourceActions: [{ resourceOperation: ResourceOperations.Read, resourceType: ResourceTypes.Interview }],
    tenantAddonEnabled: ['samtaleforberedelseSDP'],
    isPossiblyRestricted: true,
    employmentOpticOnly: true,
    hideForAnonymousCitizen: true,
  },
  {
    id: '3f129728-AC32-4d65-94b9-c5a7c03fb022',
    label: 'Samtaleforberedelse: Aktivitetsparate',
    context: 'citizen',
    group: 'Borgerhandling',
    callbackFn: (contextId: string) => {
      window.open(`/deeplink/citizen/${contextId}/interview-preparation-activity`);
    },
    resourceActions: [{ resourceOperation: ResourceOperations.Read, resourceType: ResourceTypes.Interview }],
    tenantAddonEnabled: ['samtaleforberedelseAktivitetsParate'],
    isPossiblyRestricted: true,
    employmentOpticOnly: true,
    hideForAnonymousCitizen: true,
  },
  {
    id: '9b54dae2-5b00-4133-8777-d51254217d8b',
    label: 'Opret aktivitet',
    keywords: ['tilbud', 'forløb'],
    context: 'citizen',
    group: 'Borgerhandling',
    callbackFn: (contextId: string, _crmUrl, _tenantName, _spuid, opticId?: string) => {
      const baseUrl = `/deeplink/offer-and-activity/create-activity-offer-select?citizenId=${contextId}`;
      window.open(opticId ? `${baseUrl}&${OPTIC_URL_PARAM}=${opticId}` : baseUrl);
    },
    resourceActions: [{ resourceOperation: ResourceOperations.Write, resourceType: ResourceTypes.Activity }],
  },
  {
    id: 'a8fa99cd-098e-4042-ae65-9778aa18ee6f',
    label: 'Opret journalnotat',
    keywords: ['notat', 'sagsnotat'],
    context: 'citizen',
    group: 'Borgerhandling',
    callbackFn: (contextId: string, _crmUrl, _tenantName, _spuid, opticId?: string) => {
      const baseUrl = `/deeplink/standalone/citizen/${contextId}/journal/note/create`;
      window.open(opticId ? `${baseUrl}?${OPTIC_URL_PARAM}=${opticId}` : baseUrl);
    },
    resourceActions: [{ resourceOperation: ResourceOperations.Write, resourceType: ResourceTypes.JournalNote }],
  },
  {
    id: '96f52adc-777d-4608-a190-d5a4e74d1e5f',
    label: 'Opret kontaktgruppe',
    context: 'citizen',
    group: 'Borgerhandling',
    callbackFn: (contextId: string) => {
      window.open(`/deeplink/standalone/citizen/${contextId}/contact-group/create`);
    },
    resourceActions: [{ resourceOperation: ResourceOperations.Write, resourceType: ResourceTypes.ContactGroup }],
    employmentOpticOnly: true,
    hideForAnonymousCitizen: true,
  },
  {
    id: '7210d3c2-1f27-4d12-bf70-1f8f4ae034b8',
    label: 'Opret helbredsbegrænsning',
    keywords: ['skånehensyn', 'helbred'],
    context: 'citizen',
    group: 'Borgerhandling',
    callbackFn: (contextId: string) => {
      window.open(`/deeplink/standalone/citizen/${contextId}/health-limitation/create`);
    },
    resourceActions: [{ resourceOperation: ResourceOperations.Write, resourceType: ResourceTypes.Availability }],
    employmentOpticOnly: true,
    hideForAnonymousCitizen: true,
  },
  {
    id: '3f129728-eb33-4d65-94b9-c5a7c03fb022',
    label: 'Opstart helhedsorientet indsats',
    context: 'citizen',
    group: 'Borgerhandling',
    callbackFn: (contextId: string) => {
      window.open(`/deeplink/citizen/${contextId}/social-integration/create-hoi-guide`);
    },
    resourceActions: [{ resourceOperation: ResourceOperations.Write, resourceType: ResourceTypes.Case }],
    tenantAddonEnabled: ['helhedsorienteretindsats'],
    isPossiblyRestricted: true,
    availabilityHint: 'Kræver, at borgeren er kendt i Nexus',
  },
  {
    id: 'b8c00dd9-0b8e-43fe-8460-1065ee5f356c',
    label: 'Plan & Aktiviteter',
    context: 'citizen',
    group: 'Borgerområde',
    url: '/deeplink/citizen/:contextId/plan/view',
    resourceActions: [{ resourceOperation: ResourceOperations.JournalNote.Create, resourceType: ResourceTypes.JournalNote }],
  },
  {
    id: '98e966c5-cfaa-47ce-98c4-9b475d22bfa7',
    label: 'Registrér underretningspligtig hændelse',
    context: 'citizen',
    group: 'Borgerhandling',
    url: `/deeplink/citizen/:contextId/${negativeEventCreatePath}`,
    resourceActions: [{ resourceOperation: ResourceOperations.Write, resourceType: ResourceTypes.Availability }],
    employmentOpticOnly: true,
    hideForAnonymousCitizen: true,
  },
  {
    id: 'a907d734-1286-419a-8cee-9ae86caad544',
    label: 'Rådighed & Fritagelser',
    keywords: ['fritagelse', 'sanktion'],
    context: 'citizen',
    group: 'Borgerområde',
    url: '/deeplink/citizen/:contextId/citizen-availability',
    resourceActions: [{ resourceOperation: ResourceOperations.JournalNote.Create, resourceType: ResourceTypes.JournalNote }],
    employmentOpticOnly: true,
    hideForAnonymousCitizen: true,
  },
  {
    id: 'a0ced348-c972-415d-832b-bbd4cbde96ec',
    label: 'Sager',
    context: 'none',
    group: 'Borgerområde',
    url: '/deeplink/citizens/cases',
    resourceActions: [{ resourceOperation: ResourceOperations.JournalNote.Create, resourceType: ResourceTypes.JournalNote }],
  },
  {
    id: '58fbb8e6-9110-433c-aa9e-c7cf45777751',
    label: 'Samtaler',
    context: 'citizen',
    group: 'Borgerområde',
    url: '/deeplink/citizen/:contextId/interview',
    resourceActions: [{ resourceOperation: ResourceOperations.JournalNote.Create, resourceType: ResourceTypes.JournalNote }],
  },
  {
    id: '5c8e1e50-ca87-4abe-975a-bfe5205e804b',
    label: 'Indkald til samtale',
    keywords: ['indkaldelse', 'møde', 'booking'],
    context: 'citizen',
    group: 'Borgerhandling',
    callbackFn: (contextId: string, _crmUrl, _tenantName, _spuid, opticId?: string) => {
      const baseUrl = `/deeplink/standalone/citizen/${contextId}/interview/${bookInterviewV2Path}`;
      window.open(opticId ? `${baseUrl}?${OPTIC_URL_PARAM}=${opticId}` : baseUrl);
    },
    resourceActions: [{ resourceOperation: ResourceOperations.Write, resourceType: ResourceTypes.Interview }],
    isConditionallyEnabled: true,
    availabilityHint: 'Kræver en aktiv sag med en målgruppe, der tillader samtaler',
  },
  {
    id: 'd95ca6c2-887a-4fcd-b854-fb339dd71430',
    label: 'Gennemfør samtale',
    keywords: ['møde'],
    context: 'citizen',
    group: 'Borgerhandling',
    callbackFn: (contextId: string, _crmUrl, _tenantName, _spuid, opticId?: string) => {
      const baseUrl = `/deeplink/standalone/citizen/${contextId}/interview/guide-conduct-interview`;
      window.open(opticId ? `${baseUrl}?${OPTIC_URL_PARAM}=${opticId}` : baseUrl);
    },
    isConditionallyEnabled: true,
    resourceActions: [{ resourceOperation: ResourceOperations.Write, resourceType: ResourceTypes.Interview }],
    availabilityHint: 'Kræver en aktiv sag med en målgruppe, der tillader samtaler, og at borgeren er grupperet',
  },
  {
    id: '90ec834b-cde6-431d-be51-bba9d6c2f5df',
    label: 'Udsted frist',
    keywords: ['deadline'],
    context: 'citizen',
    group: 'Borgerhandling',
    callbackFn: (contextId: string) => {
      window.open(`/deeplink/standalone/citizen/${contextId}/interview/guide-interview-deadline`);
    },
    resourceActions: [{ resourceOperation: ResourceOperations.Write, resourceType: ResourceTypes.InterviewDeadline }],
    employmentOpticOnly: true,
    hideForAnonymousCitizen: true,
  },
  {
    id: '41f66ef0-88fc-433e-911f-5d18ec0dfafb',
    label: 'Opret advis',
    keywords: ['påmindelse'],
    context: 'citizen',
    group: 'Borgerhandling',
    callbackFn: (contextId: string) => {
      window.open(`/deeplink/standalone/citizen/${contextId}/tasks/create?templateType=Advis`);
    },
    resourceActions: [{ resourceOperation: ResourceOperations.Write, resourceType: ResourceTypes.CitizenTask }],
  },
  {
    id: '04c11af4-b80e-4477-9365-074a2054e62d',
    label: 'Opret opgave',
    keywords: ['påmindelse', 'todo'],
    context: 'citizen',
    group: 'Borgerhandling',
    callbackFn: (contextId: string) => {
      window.open(`/deeplink/standalone/citizen/${contextId}/tasks/create`);
    },
    resourceActions: [{ resourceOperation: ResourceOperations.Write, resourceType: ResourceTypes.CitizenTask }],
  },
  {
    id: 'e9503613-a80b-41ea-b366-46c0a30d4ffb',
    label: 'Opret negativ hændelse',
    keywords: ['sanktion'],
    context: 'citizen',
    group: 'Borgerhandling',
    callbackFn: (contextId: string) => {
      window.open(`/deeplink/standalone/citizen/${contextId}/negative-event/create`);
    },
    resourceActions: [{ resourceOperation: ResourceOperations.Write, resourceType: ResourceTypes.Availability }],
    employmentOpticOnly: true,
    hideForAnonymousCitizen: true,
  },
  {
    id: '80f966fb-83d3-47c1-8d35-62fcd9ab09d1',
    label: 'Opret gruppering',
    keywords: ['målgruppe', 'persongruppe'],
    context: 'citizen',
    group: 'Borgerhandling',
    callbackFn: (contextId: string) => {
      window.open(`/deeplink/embedded/citizen/${contextId}/interview/guide-interview-person-category`);
    },
    resourceActions: [{ resourceOperation: ResourceOperations.Write, resourceType: ResourceTypes.PersonCategory }],
    employmentOpticOnly: true,
    hideForAnonymousCitizen: true,
  },
  {
    id: '2c9942bb-8e29-4fad-80c6-4576a3d8fb8a',
    label: 'Opret afmelding',
    context: 'citizen',
    group: 'Borgerhandling',
    callbackFn: (contextId: string) => {
      window.open(`/deeplink/standalone/citizen/${contextId}/enrollment/cancel`);
    },
    resourceActions: [{ resourceOperation: ResourceOperations.Write, resourceType: ResourceTypes.Enrollment }],
    employmentOpticOnly: true,
    hideForAnonymousCitizen: true,
  },
  {
    id: '8fbb3f3b-59bd-461d-bb60-de3b5efddf89',
    label: 'Åbn Find job',
    keywords: ['jobmatch', 'stilling'],
    context: 'citizen',
    group: 'Borgerhandling',
    callbackFn: (contextId: string) => {
      window.open(`/deeplink/efforts/company-recruitments/job-match/${contextId}`);
    },
    resourceActions: [{ resourceOperation: ResourceOperations.Write, resourceType: ResourceTypes.CandidateReferral }],
    isConditionallyEnabled: true,
    employmentOpticOnly: true,
    hideForAnonymousCitizen: true,
  },
  {
    id: 'afbb3f3b-59bd-461d-bb60-de3b5efddf89',
    label: 'Opret tilmelding',
    context: 'citizen',
    group: 'Borgerhandling',
    callbackFn: (contextId: string) => {
      window.open(`/deeplink/standalone/citizen/${contextId}/enrollment/enroll`);
    },
    resourceActions: [{ resourceOperation: ResourceOperations.Write, resourceType: ResourceTypes.Enrollment }],
    employmentOpticOnly: true,
    hideForAnonymousCitizen: true,
  },
  {
    id: '293de945-2cd2-4f48-b23d-afc69090c12c',
    label: 'Opret fravær',
    keywords: ['sygdom', 'ferie'],
    context: 'citizen',
    group: 'Borgerhandling',
    callbackFn: (contextId: string) => {
      window.open(`/deeplink/standalone/citizen/${contextId}/absence/create`);
    },
    resourceActions: [{ resourceOperation: ResourceOperations.Write, resourceType: ResourceTypes.Availability }],
    isPossiblyRestricted: true,
    employmentOpticOnly: true,
    hideForAnonymousCitizen: true,
  },
  {
    id: '8fbb373b-59bd-461d-bb60-de3b5efddf89',
    label: 'Opret journalnotat',
    keywords: ['notat', 'sagsnotat'],
    context: 'citizen',
    group: 'Borgerhandling',
    callbackFn: (contextId: string, _crmUrl, _tenantName, _spuid, opticId?: string) => {
      const baseUrl = `/deeplink/standalone/citizen/${contextId}/journal/note/create`;
      window.open(opticId ? `${baseUrl}?${OPTIC_URL_PARAM}=${opticId}` : baseUrl);
    },
    resourceActions: [{ resourceOperation: ResourceOperations.JournalNote.Create, resourceType: ResourceTypes.JournalNote }],
  },
  {
    id: '1347277f-39ff-445b-8758-80070c95eada',
    label: 'Aktindsigt',
    context: 'citizen',
    group: 'Borgerområde',
    url: '/deeplink/citizen/:contextId/journal?omnibar-link=aktindsigter',
    resourceActions: [{ resourceOperation: ResourceOperations.JournalNote.Create, resourceType: ResourceTypes.JournalNote }],
  },
  {
    id: 'c62ce676-e8ae-4235-a764-13308fd982c2',
    label: 'Kandidatformidlinger',
    context: 'citizen',
    group: 'Borgerområde',
    url: '/deeplink/citizen/:contextId/cvjobsearch?omnibar-link=kandidatformidlinger',
    resourceActions: [{ resourceOperation: ResourceOperations.JournalNote.Create, resourceType: ResourceTypes.JournalNote }],
    employmentOpticOnly: true,
    hideForAnonymousCitizen: true,
  },
  {
    id: 'b9b00dd9-0b8e-43fe-8460-1065ee5f356c',
    label: 'Kontrakt (Integration)',
    actionType: 'Redigér',
    context: 'citizen',
    group: 'Borgerhandling',
    callbackFn: (contextId: string) => {
      window.open(`/deeplink/standalone/citizen/${contextId}/contract/edit`);
    },
    resourceActions: [{ resourceOperation: ResourceOperations.Read, resourceType: ResourceTypes.Citizen }],
    employmentOpticOnly: true,
  },
  {
    id: '24ca709c-5914-42cb-a5bb-ef8a67734f3a',
    label: 'Opret sag',
    context: 'citizen',
    group: 'Saghandling',
    url: '/deeplink/citizen/:contextId/details?omnibar-link=createcase',
    resourceActions: [{ resourceOperation: ResourceOperations.Write, resourceType: ResourceTypes.Citizen }],
  },
  {
    id: '24ca709c-5914-42cb-a5bb-ef8a67734f3a',
    label: 'Opret persongruppemarkering',
    context: 'citizen',
    group: 'Persongruppemarkering',
    url: '/deeplink/citizen/:contextId/details?omnibar-link=createpersongroupmarking',
    resourceActions: [{ resourceOperation: ResourceOperations.JournalNote.Create, resourceType: ResourceTypes.JournalNote }],
  },
  {
    id: '24ca7dse-5914-42cb-a5bb-ef8a67734f3a',
    label: 'Opret underretning til A-kasse',
    context: 'citizen',
    group: 'A-Borgerområde',
    callbackFn: (contextId: string) => {
      window.open(`/deeplink/standalone/citizen/${contextId}/unemploymentbenefits/nuph`);
    },
    resourceActions: [{ resourceOperation: ResourceOperations.Read, resourceType: ResourceTypes.Citizen }],
    employmentOpticOnly: true,
    hideForAnonymousCitizen: true,
  },
  {
    id: '24ca709c-5914-42cb-a5bb-ef8a67734f29',
    label: 'Skift jobcenter',
    context: 'citizen',
    group: 'Borgerhandling',
    getModalControl: (contextId: string) => <OmnibarChangeJobcenter forceRefresh={new Date()} citizenId={contextId} />,
    resourceActions: [{ resourceOperation: ResourceOperations.Read, resourceType: ResourceTypes.Citizen }],
  },
  {
    id: '24ca709c-5914-42cb-a5bb-ef8a67734f3b',
    label: 'Vis forsørgelseshistorik',
    context: 'citizen',
    group: 'Borgerhandling',
    callbackFn: (contextId: string) => {
      window.open(`citizen/authority/${contextId}/dfdg-sustenance-history/year`);
    },
    resourceActions: [{ resourceOperation: ResourceOperations.Read, resourceType: ResourceTypes.Citizen }],
    employmentOpticOnly: true,
    hideForAnonymousCitizen: true,
  },
  {
    id: '24ca709c-5914-42cb-a5bb-ef8a67734f3c',
    label: 'Vis ungedatabasen',
    context: 'citizen',
    group: 'Borgerhandling',
    callbackFn: (contextId: string) => {
      window.open(`/deeplink/standalone/citizen/${contextId}/communication`);
    },
    resourceActions: [{ resourceOperation: ResourceOperations.Read, resourceType: ResourceTypes.Citizen }],
    hideForAnonymousCitizen: true,
    employmentOpticOnly: true,
  },
  {
    id: '24ca709c-5914-42cb-a5bb-ef8a67734f3d',
    label: 'Vis jobcenterhistorik',
    context: 'citizen',
    group: 'Borgerhandling',
    callbackFn: (contextId: string, crmUrl: string, tenantName: string) => {
      const url = `${crmUrl}/${tenantName}/Person/PersonHistoryModernized/JobcenterHistory?contactId={${contextId}}`;
      window.open(url);
    },
    resourceActions: [{ resourceOperation: ResourceOperations.Read, resourceType: ResourceTypes.Citizen }],
    employmentOpticOnly: true,
    hideForAnonymousCitizen: true,
  },
  {
    id: '24ca709c-5914-42cb-a5bb-ef8a67734f30',
    label: 'Vis tilmeldingshistorik',
    context: 'citizen',
    group: 'Borgerhandling',
    callbackFn: (contextId: string) => {
      window.open(`${citizenAreaPathName}/${contextId}/${dfdgHistoryViewerPath}/Tilmeldinger`);
    },
    resourceActions: [{ resourceOperation: ResourceOperations.Read, resourceType: ResourceTypes.Citizen }],
    employmentOpticOnly: true,
    hideForAnonymousCitizen: true,
  },
  {
    id: '24ca709c-5914-42cb-a5bb-ef8a67734f3f',
    label: 'Vis kommentarer',
    keywords: ['bemærkning'],
    context: 'citizen',
    group: 'Borgerhandling',
    callbackFn: (contextId: string, crmUrl: string, tenantName: string) => {
      window.open(`/deeplink/standalone/citizen/${contextId}/citizencomments`);
    },
    resourceActions: [{ resourceOperation: ResourceOperations.Read, resourceType: ResourceTypes.Citizen }],
    employmentOpticOnly: true,
    hideForAnonymousCitizen: true,
  },
  {
    id: '24ca709c-5914-42cb-a5bb-ef8a67734f3g',
    label: 'Vis dialogmodul',
    keywords: ['besked', 'chat'],
    context: 'citizen',
    group: 'Borgerhandling',
    callbackFn: (contextId: string, crmUrl: string, tenantName: string) => {
      const url = `${crmUrl}/${tenantName}/Person/CitizenMessagesModernized/Index?contactId={${contextId}}`;
      window.open(url);
    },
    resourceActions: [{ resourceOperation: ResourceOperations.Read, resourceType: ResourceTypes.Citizen }],
  },
  {
    id: '24ca709c-5914-42cb-a5bb-ef8a67734f3e',
    label: 'Vis tilmeldinger',
    context: 'citizen',
    group: 'Borgerhandling',
    url: '/deeplink/citizen/:contextId/cvjobsearch?omnibar-link=showEnrollments',
    resourceActions: [{ resourceOperation: ResourceOperations.Read, resourceType: ResourceTypes.Citizen }],
    employmentOpticOnly: true,
    hideForAnonymousCitizen: true,
  },
  {
    id: '8fbb373b-59xx-461d-bb60-de3b5efddf89',
    label: 'Opret borgers kontaktperson',
    context: 'citizen',
    group: 'Borgerhandling',
    callbackFn: (contextId: string, _crmUrl, _tenantName, _spuid, opticId?: string) => {
      const baseUrl = `/deeplink/standalone/citizen/${contextId}/citizen-person-contact`;
      window.open(opticId ? `${baseUrl}?${OPTIC_URL_PARAM}=${opticId}` : baseUrl);
    },
    resourceActions: [
      { resourceOperation: ResourceOperations.Write, resourceType: ResourceTypes.Citizen },
      { resourceOperation: ResourceOperations.ServiceProviderCitizenDiary.Create, resourceType: ResourceTypes.ServiceProviderCitizenDiary },
    ],
  },
  {
    id: '5f2d8c3a-9e41-4b7a-8c06-1a7e3b9d4f25',
    label: 'Opret borgers kontaktperson',
    context: 'citizen',
    citizenArea: 'serviceprovider',
    group: 'Borgerhandling',
    callbackFn: (contextId: string, _crmUrl: string, _tenantName: string, serviceProviderUnitId?: string) => {
      const baseUrl = `/citizen/serviceprovider/${contextId}/citizen-person-contact`;
      window.open(serviceProviderUnitId ? `${baseUrl}?spuid=${serviceProviderUnitId}` : baseUrl);
    },
    featureToggleRequired: catalogCorrectionsToggle,
    resourceActions: [{ resourceOperation: ResourceOperations.ServiceProviderCitizenDiary.Create, resourceType: ResourceTypes.ServiceProviderCitizenDiary }],
  },
  {
    id: 'e387348d-3513-4417-99eb-123680b8b721',
    label: 'Opret dagbogsnotat',
    keywords: ['notat', 'journalnotat'],
    context: 'citizen',
    citizenArea: 'serviceprovider',
    group: 'Borgerhandling',
    callbackFn: (contextId: string, _crmUrl: string, _tenantName: string, serviceProviderUnitId?: string) => {
      const baseUrl = `/deeplink/citizen/serviceprovider/${contextId}/diary/note/create`;
      const url = serviceProviderUnitId ? `${baseUrl}?spuid=${serviceProviderUnitId}` : baseUrl;
      window.open(url);
    },
    resourceActions: [{ resourceOperation: ResourceOperations.ServiceProviderCitizenDiary.Create, resourceType: ResourceTypes.ServiceProviderCitizenDiary }],
  },
  {
    id: 'e376548d-3513-4417-99eb-123680b8b721',
    label: 'Dan dokument',
    context: 'citizen',
    citizenArea: 'serviceprovider',
    group: 'Borgerhandling',
    callbackFn: (contextId: string, _crmUrl: string, _tenantName: string, serviceProviderUnitId?: string) => {
      const baseUrl = `/deeplink/citizen/serviceprovider/${contextId}/diary/document/create`;
      const url = serviceProviderUnitId ? `${baseUrl}?spuid=${serviceProviderUnitId}` : baseUrl;
      window.open(url);
    },
    resourceActions: [{ resourceOperation: ResourceOperations.ServiceProviderCitizenDiary.Create, resourceType: ResourceTypes.ServiceProviderCitizenDiary }],
  },
  {
    id: 'e376548d-3513-4417-99eb-123680987651',
    label: 'Dan brev',
    keywords: ['skrivelse', 'digital post'],
    context: 'citizen',
    citizenArea: 'serviceprovider',
    group: 'Borgerhandling',
    callbackFn: (contextId: string, _crmUrl: string, _tenantName: string, serviceProviderUnitId?: string) => {
      const baseUrl = `/deeplink/citizen/serviceprovider/${contextId}/communication/guide-letter-social-provider`;
      const url = serviceProviderUnitId ? `${baseUrl}?spuid=${serviceProviderUnitId}` : baseUrl;
      window.open(url);
    },
    resourceActions: [{ resourceOperation: ResourceOperations.ServiceProviderCitizenDiary.Create, resourceType: ResourceTypes.ServiceProviderCitizenDiary }],
  },
  {
    id: '24ddddse-5914-42cb-a5bb-ef8a67734f3a',
    label: 'Vis Aktivitetshistorik',
    context: 'citizen',
    group: 'Borgerhandling',
    callbackFn: (contextId: string) => {
      window.open(`/deeplink/standalone/citizen/${contextId}/activity-history`);
    },
    resourceActions: [{ resourceOperation: ResourceOperations.Read, resourceType: ResourceTypes.Citizen }],
    employmentOpticOnly: true,
    hideForAnonymousCitizen: true,
  },
];

export default functions;
